import React, { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import AnimatedTitle from '../components/AnimatedTitle';
import videoSrc from '../videos/Video2Space.mp4';
import { Button, IconButton, List, ListItem, ListItemIcon, ListItemText, Modal, Paper, Typography, useTheme } from '@mui/material';
import Novel3DViewer from '../components/Novel3dViewer';
import { Book } from '../types/book';
import { ArrowBack, ArrowForward, Close, Facebook, FormatQuote, Headphones, Instagram } from '@mui/icons-material';
import { AmazonCircleFilled, StarFilled, TikTokOutlined } from '@ant-design/icons';
import ResponsiveAppBar from '../components/ResponsiveAppBar';
import { useLocation, useNavigate } from 'react-router-dom';
import koboBookLogo from '../images/kobo_logo.svg'
import SignupForm from './SignupForm';
import signUpImage from '../images/Ship Image.png'

  // Book purchasing and listening platforms
export const bookPlatforms = [
  // Physical & Digital Book Stores
  { name: 'Amazon', url: 'https://www.amazon.com/dp/B0G3Q7LP2L', logo: <AmazonCircleFilled  style={{fontSize:'48px', color:'black'}}/>, color:'#FF9900' },
  // { name: 'Apple Books', url: 'https://books.apple.com', logo: <img src={appleBooksLogoUrl} alt="Apple Books" style={{width:'48px', height:'48px'}} />, color:'#FFFFFF'},
  // { name: 'Google Play Books', url: 'https://play.google.com/store/books', logo: <img src={googlePlayBooks} alt="Google Play Books" style={{width:'48px', height:'48px'}} />, color:'#4285F4' },
  // { name: 'Barnes & Noble', url: 'https://play.google.com/store/books', logo: <img src={barnesLogo} alt="Barnes & Noble" style={{width:'48px', height:'48px'}} />, color:'#366251' },
  { name: 'Kobo', url: 'https://www.kobo.com/nz/en/ebook/shard-awakening', logo: <img src={koboBookLogo} alt="Kobo Books" style={{width:'48px', height:'48px'}} />, color:'#BF0000' },
];

const audiobookPlatforms = [
  {
    name: 'Audible',
    url: 'https://www.audible.co.uk/pd/Shard-Awakening-Audiobook/B0HGQD1Q4M',
    logo: <Headphones sx={{ fontSize: 48, color: '#111' }} />,
    color: '#F8991D',
  },
  {
    name: 'Kobo Audiobooks',
    url: 'https://www.kobo.com/nz/en/audiobook/shard-awakening-1',
    logo: <img src={koboBookLogo} alt="Kobo Audiobooks" style={{width:'48px', height:'48px'}} />,
    color: '#BF0000',
  },
];

const getBookPlatforms = (book: Book) => {
  const platforms = [];
  
  if (book.platforms?.amazon) {
    platforms.push({
      name: 'Amazon',
      url: book.platforms.amazon,
      logo: <AmazonCircleFilled style={{fontSize:'48px', color:'black'}}/>,
      color: '#FF9900'
    });
  }
  
  if (book.platforms?.kobo) {
    platforms.push({
      name: 'Kobo',
      url: book.platforms.kobo,
      logo: <img src={koboBookLogo} alt="Kobo Books" style={{width:'48px', height:'48px'}} />,
      color: '#BF0000'
    });
  }
  
  return platforms;
};

const HomeSection: React.FC = () => {
  const navigate = useNavigate();

  // State to track the overall video opacity (can be influenced by scroll or ending)
  const [videoOpacity, setVideoOpacity] = useState(1);
  // State to track if the video playback has ended
  const [videoPlaybackEnded, setVideoPlaybackEnded] = useState(false);
  // State to control the fading specifically when playback ends
  const [videoEndingFade, setVideoEndingFade] = useState(1); // 1 means fully visible, will fade to 0
  const [currentBookIndex, setCurrentBookIndex] = useState(0);


  const [retailerModal, setRetailerModal] = useState<'book' | 'audiobook' | null>(null);
  const books: Book[] = [
    {
      title: "Shard Awakening",
      description: `Two children. One secret. A galaxy on the brink of war.

In the dying light of a shattered empire, a royal family risks everything to protect newborn twins, children born with a rare and dangerous gift known as the Shard. Once thought to be a disease, the Devourer Virus is far more than that: it is ancient, intelligent... and alive.

As news of the birth leaks, enemies close in. Assassins disguised as allies. Soldiers who bleed anything but red. And a Federation willing to burn entire worlds to control what the twins carry in their blood.

Their only chance of survival lies on Earth, a planet erased from galactic records, hidden for centuries, and long considered lost. But Earth is no sanctuary. Beneath its surface, something dark stirs, a sentient force older than civilization, buried in silence and waiting to rise again.

What begins as a desperate escape becomes a revelation that changes everything. Because the twins are not just infected, they are awakening. And what they become could either unite the galaxy... or bring about its extinction.

As the line between friend and enemy blurs, and a vast conspiracy unravels, the Shard's true purpose is revealed. And with it, the return of an ancient war that never truly ended.

Shard Awakening is a high-octane fusion of sci-fi thriller, space opera, and horror, launching an epic saga of betrayal, forgotten history, and a power too great to be controlled.`,
      modelPath: "/models/Book.glb",
      viewerTitle: "Featured Novel: Shard Awakening",
      viewerDescription: "A sci-fi thriller about a lost civilization.",
      disabled: false,
      slug: "shard-awakening",
      coverImageUrl: "/images/cover.jpg",
      featuredText: `When Max and Chloe uncover the nanites inside them, their lives change forever.
  Ascension is their only chance to survive. Fail, and the nanites will consume them from within.

  But time is running out. Far beyond Earth, their empire may already lie in ruins. The enemy is stronger, smarter—and watching. Feeding. Waiting.`,
      availableDate: "Available Worldwide Now",
      preorderLink: "https://www.amazon.com/dp/B0G3Q7LP2L",
      // sampleLink: "https://www.wattpad.com/1572831333-shard-awakening-book-1-chapter-1-the-escape",
      reviewLink: "https://www.amazon.com/dp/B0G3Q7LP2L",
      hook: "The journey begins...",
      promoVideo: '/Book1Trailer.mp4',
      platforms: {
        amazon: 'https://www.amazon.com/dp/B0G3Q7LP2L', // Update with actual URL
        kobo: 'https://www.kobo.com/nz/en/ebook/shard-awakening',
        goodreads: 'https://www.goodreads.com/author/show/62379421.Martin_Knaggs'
      },
      isAvaliable: true,
      // Add reviews for Book 1:
      reviews: [
        {
          text: "Cinematic, tense, and impossible to put down. I read it in just a few sittings. It stayed with me long after I finished.",
          author: "Early Reader"
        },
        {
          text: "A rare mix of heart and high-stakes sci-fi. Max and Chloe feel real, and unforgettable. The emotional weight makes every page hit harder.",
          author: "Early Reader"
        },
        {
          text: "Dark, haunting, and beautifully written. I was drawn in from the first chapter and stayed with it to the end. An impressive debut from a first-time author.",
          author: "Early Reader"
        }
      ]
    },
    {
  title: "Shard Emergence",
  description: `Two heirs to a dying empire. One destiny they can't outrun.

On Earth, Max and Chloe train to master the nanites inside them, a power that could one day save worlds… or consume them from within. Ascension is their only chance to survive. If they fail, the nanites will tear them apart.

But the clock is already running out. Far beyond Earth, a war rages, and it may be over before they return. The enemy they face is not only stronger but smarter, and every move it makes draws them closer to the front lines.

And in the shadows, something is watching. Feeding. Waiting.

Since its release, the entity’s hunger has grown, and so has its cunning. It does not just feed on life. It steals memories, emotions, and the very essence of its victims. Now it hunts with purpose, with a plan to destroy and to claim the twins for its own.

The more they train, the more they discover the terrible cost of their abilities. Powers meant to protect could just as easily destroy, and the line between hero and weapon blurs with every lesson learned.

They cannot hide from what is coming. They can only decide who they will be when it arrives.

They are the empire’s last hope…`,
  modelPath: "/models/Book2.glb",
  viewerTitle: "Shard Legacy Book 2",
  viewerDescription: "An Alien Invasion Story: Coming Soon",
  disabled: false,
  slug: "shard-series-2",
  coverImageUrl: "/images/book2_cover.jpg",
  downloadLink: "https://google.com",
  preorderLink: "https://google.com",
  platforms: {
    amazon: 'https://www.amazon.com/dp/B0G3Q7LP2L', // Update with actual URL
  },
  featuredText: `The Guardians were never meant to stay hidden. Max and Chloe know this truth now—every training session in the Colorado mountains, every grueling test of body and mind, is only sharpening them for what's coming.

Because beyond Earth, the galaxy is shifting. Empires fracture, alliances fail, and an ancient hunger stirs in the dark. It doesn't bleed its prey—it empties them, stealing memory, fear, and the very spark of who they are. Fire alone kept it at bay once, but next time, it will be ready.

As war edges closer, the twins must decide what kind of leaders they will become. Will their bond make them stronger… or break them when the shadows strike?

The Shard Legacy continues with Book Two: a story of survival, sacrifice, and the terrifying cost of power.`,
  availableDate: "Coming Nov 2026",
  hook: "THEIR POWER IS AWAKENING. THEIR ENEMIES ARE ALREADY HERE.",
  promoImage: '/Book2Promo.png',
  popupKey: '0zkRNp',
  reviewLink: "https://google.com",
  isAvaliable: false,
  // No reviews for Book 2 - will show "Coming Soon"
    },
    {
      title: "Shard Crucible",
      description: "An Alien Invasion Story: Coming Soon",
      modelPath: "/models/Book3.glb",
      viewerTitle: "Shard Legacy Book 3",
      viewerDescription: "An Alien Invasion Story: Coming Soon",
      disabled: false,
      slug: "shard-series-3",
      coverImageUrl: "/images/book3_cover.jpg",
      featuredText: '',
      availableDate: "Coming Mid 2026",
      isAvaliable: false,
      // No reviews for Book 3 - will show "Coming Soon"
      hook: `They have trained.
      They have endured.
      Now the war that matters begins.
      
      The crucible awaits.
      It will forge them… or break them.`,
    },
  ];

  // Effect for scroll-based opacity
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const fadeStart = 100; // Start fading from the top of the section
      const fadeEnd = 500; // End fading after scrolling 500px (adjust as needed)

      let newOpacity = 1 - (scrollY - fadeStart) / (fadeEnd - fadeStart);
      newOpacity = Math.max(0, Math.min(1, newOpacity));

      // We only update the main videoOpacity based on scroll IF the video hasn't ended playback yet
      if (!videoPlaybackEnded) {
         setVideoOpacity(newOpacity);
      }
      // If videoPlaybackEnded is true, the opacity is controlled by videoEndingFade, not scroll
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [videoPlaybackEnded]); // Re-run effect if videoPlaybackEnded changes

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const theme = useTheme();
  // Effect to handle the fade-out when video playback ends
  useEffect(() => {
    if (videoPlaybackEnded) {
      // Start the fade-out animation shortly after the video ends
      // You can adjust the timeout duration if needed
      const fadeTimeout = setTimeout(() => {
        setVideoEndingFade(0); // Set opacity to 0 to trigger CSS transition
      }, 100); // Small delay to ensure state update

      // Clear the timeout if the component unmounts before it fires
      return () => clearTimeout(fadeTimeout);
    }
  }, [videoPlaybackEnded]); // This effect runs when videoPlaybackEnded becomes true

  // Calculate the final opacity: If video has ended playback, use the ending fade value,
  // otherwise use the scroll-based opacity.
  const finalVideoOpacity = videoPlaybackEnded ? videoEndingFade : videoOpacity;

  const handleNavClick = (id: string): void => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const handleClose = () => setRetailerModal(null);

  const handlePlatformClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };


  return (
    <Box id="home">
      <ResponsiveAppBar onNavClick={handleNavClick} />
      <Box>
        <Box>
          <video
            autoPlay
            muted
            // Set videoPlaybackEnded to true when the video naturally finishes
            onEnded={() => setVideoPlaybackEnded(true)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100vh',
              objectFit: 'cover',
              zIndex: 0,
              // Use the calculated final opacity
              opacity: finalVideoOpacity,
              // Add a transition for the opacity change when fading out
              transition: 'opacity 1s ease-out', // Adjust duration and timing function as needed
            }}
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        </Box>
        {/* Main content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, alignItems: 'center', display: 'flex', height: '100vh', width: '100%',justifyContent: 'center' }}>
          <AnimatedTitle text="MARTIN KNAGGS" />
        </Container>
      </Box>
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' }, minHeight: '800px' }}>
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          height: '100%',
          gap: { xs: 4, md: 0 },
          minHeight: '600px'
        }}>
          <Box sx={{lineHeight: '0.0', flex: { xs: 1, md: 0.6 }}}>

              {currentBookIndex === 0 ? (
                <>
                  <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700} color={theme.palette.primary.main}>
                    TWO HEIRS TO A DYING EMPIRE.
                  </Typography><br/>
                  <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700} color={theme.palette.primary.main}>
                    ONE DESTINY THEY CAN'T OUTRUN.
                  </Typography><br/>
                </>
              ) : (
                <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700} color={theme.palette.primary.main}>
                  {books[currentBookIndex].hook}
                </Typography>
              )}

            <Typography fontSize={{ xs: 24, sm: 30, md: 40 }} fontWeight={700} color={theme.palette.primary.main}>
              Featured Book: {books[currentBookIndex].title}
            </Typography>

            {currentBookIndex === 0 ? (
            <Typography fontSize={{ xs: 24, sm: 30, md: 40 }} fontWeight={700} sx={{color: theme.palette.secondary.main}}>
              {books[currentBookIndex].hook}
            </Typography>
            ) : <></>}

            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400} sx={{marginTop: '5%'}} color='black'>
              {books[currentBookIndex].featuredText}<br/><br/>
              {books[currentBookIndex].availableDate}
            </Typography>
            {(books[currentBookIndex].promoImage || books[currentBookIndex].promoVideo) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  alignItems: 'center',
                  flex: 1,
                  marginTop: '10px',
                  gap: 2,
                }}
              >
                {books[currentBookIndex].promoImage && (
                  <a 
                    href='https://www.amazon.com/dp/B0G3Q7LP2L'
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={books[currentBookIndex].promoImage}
                      alt={`${books[currentBookIndex].title} promo`}
                      style={{
                        width: '350px',
                        height: '230px',
                        objectFit: 'fill',
                        borderRadius: '8px',
                        cursor: 'pointer',
                      }}
                    />
                  </a>
                )}
                {books[currentBookIndex].promoVideo && (
                  <video
                    src={books[currentBookIndex].promoVideo}
                    controls
                    style={{
                      width: '350px',
                      height: '230px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                    }}
                  />
                )}
              </Box>
            )}
            {currentBookIndex === 0 && (
              <Paper
                component="section"
                aria-labelledby="audiobook-sample-title"
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: 3,
                  mt: 3,
                  p: 2.5,
                  maxWidth: 720,
                  background: 'rgba(255,255,255,0.45)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(85, 222, 241, 0.7)',
                }}
              >
                <Box
                  component="img"
                  src="/images/shard-awakening-audiobook.jpg"
                  alt="Shard Awakening audiobook cover"
                  sx={{
                    width: { xs: '100%', sm: 210 },
                    maxWidth: 280,
                    aspectRatio: '1 / 1',
                    objectFit: 'cover',
                    borderRadius: 1.5,
                    boxShadow: 4,
                  }}
                />
                <Box sx={{ flex: 1, width: '100%', lineHeight: 'normal' }}>
                  <Typography
                    id="audiobook-sample-title"
                    variant="h5"
                    component="h3"
                    fontWeight={700}
                    sx={{ color: theme.palette.secondary.main }}
                  >
                    Listen to the Audiobook Sample
                  </Typography>
                  <Typography sx={{ mt: 1, mb: 2, color: 'black' }}>
                    Chapter One, narrated by Sean Antony
                  </Typography>
                  <Box
                    component="audio"
                    controls
                    preload="metadata"
                    aria-label="Play the Shard Awakening Chapter One audiobook sample"
                    sx={{ width: '100%', display: 'block' }}
                  >
                    <source src="/audio/shard-awakening-chapter-1-sample.mp3" type="audio/mpeg" />
                    Your browser does not support the audio player.
                  </Box>
                </Box>
              </Paper>
            )}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: 2,
              marginTop: '5%'
            }}>
              {/* Buttons Container */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-start' },
                gap: 2,
                flex: { xs: 1, md: 'auto' }
              }}>
                {books[currentBookIndex].preorderLink && (
                  <Button
                    variant="contained"
                    sx={{
                      padding: { xs: '15px 30px', md: '20px 48px' },
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      width:'350px',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid #55DEF1',
                      color: '#000000ff',
                      '&:hover': {
                        background: 'rgba(85, 222, 241, 0.2)',
                        backdropFilter: 'blur(15px)',
                        border: '2px solid #55DEF1',
                      }
                    }}
                    size="large"
                    onClick={()=> {
                      setRetailerModal('book');
                    }}
                  >
                    {currentBookIndex === 0 ? 'Buy the Book' : 'Pre-Order Now'}
                  </Button>
                )}
                {currentBookIndex === 0 && (
                  <Button
                    variant="contained"
                    sx={{
                      padding: { xs: '15px 30px', md: '20px 48px' },
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      width:'350px',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid #55DEF1',
                      color: '#000000ff',
                      '&:hover': {
                        background: 'rgba(85, 222, 241, 0.2)',
                        backdropFilter: 'blur(15px)',
                        border: '2px solid #55DEF1',
                      }
                    }}
                    size="large"
                    onClick={() => setRetailerModal('audiobook')}
                  >
                    Listen to the Audiobook
                  </Button>
                )}
                
                {books[currentBookIndex].sampleLink && (
                  <Button
                    variant="contained"
                    sx={{
                      padding: { xs: '15px 30px', md: '20px 48px' },
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      width:'360px',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid #55DEF1',
                      color: '#000000ff',
                      '&:hover': {
                        background: 'rgba(85, 222, 241, 0.2)',
                        backdropFilter: 'blur(15px)',
                        border: '2px solid #55DEF1',
                      }
                    }}
                    size="large"
                    href={books[currentBookIndex].sampleLink}
                  >
                    Read The Sample
                  </Button>
                )}
                
                {books[currentBookIndex].videoLink && (
                  <Button
                    variant="contained"
                    sx={{
                      padding: { xs: '15px 30px', md: '20px 48px' },
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      width:'350px',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid #55DEF1',
                      color: '#000000ff',
                      '&:hover': {
                        background: 'rgba(85, 222, 241, 0.2)',
                        backdropFilter: 'blur(15px)',
                        border: '2px solid #55DEF1',
                      }
                    }}
                    size="large"
                    href={books[currentBookIndex].videoLink}
                  >
                    WATCH VIDEO
                  </Button>
                )}
                
                {books[currentBookIndex].reviewLink && (
                  <Button
                    variant="contained"
                    sx={{
                      padding: { xs: '15px 30px', md: '20px 48px' },
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      width:'350px',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid #55DEF1',
                      color: '#000000ff',
                      '&:hover': {
                        background: 'rgba(85, 222, 241, 0.2)',
                        backdropFilter: 'blur(15px)',
                        border: '2px solid #55DEF1',
                      }
                    }}
                    size="large"
                    onClick={() => navigate(`/books/${books[currentBookIndex].slug}`, { state: { bookItem: books[currentBookIndex] } })}
                  >
                    DEEP DIVE
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
          <Modal
                open={retailerModal !== null}
                onClose={handleClose}
                aria-labelledby="retailer-platforms-modal-title"
              >
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: 'calc(100% - 32px)', sm: 500 },
                  maxHeight: '80vh',
                  backgroundColor: '#ffffffff',
                  color:"black",
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 3,
                  overflow: 'auto',
                }}>
                  <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <Close />
                  </IconButton>
                  
                  <Typography id="retailer-platforms-modal-title" variant="h5" component="h2" sx={{ mb: 3, pr: 4 }}>
                    {retailerModal === 'audiobook' ? 'Listen to the Audiobook' : 'Buy the Book'}
                  </Typography>
                  
                  {/* Book Stores Section */}
                  <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                    {retailerModal === 'audiobook' ? 'Available from' : 'Physical & Digital Retailers'}
                  </Typography>
                  <List dense>
                    {(retailerModal === 'audiobook'
                      ? audiobookPlatforms
                      : getBookPlatforms(books[currentBookIndex])
                    ).map((platform) => (
                      <ListItem
                        key={platform.name}
                        component="div"
                        onClick={() => handlePlatformClick(platform.url)}
                        sx={{
                          borderRadius: 3, // Increased for more rounded corners
                          cursor: 'pointer',
                          padding: 2.5, // Increased padding for better spacing
                          margin: 1, // Add margin between items
                          background: `linear-gradient(135deg, ${platform.color} 0%, transparent 70%)`,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            transform: 'translateY(-2px)', // Subtle lift effect on hover
                            boxShadow: 2, // Add shadow on hover
                          },
                          transition: 'all 0.2s ease-in-out', // Smooth transitions
                        }}
                      >
                        <ListItemIcon sx={{ 
                          minWidth: 56, // Increased spacing from icon
                          marginRight: 2 // Additional right margin
                        }}>
                          {platform.logo}
                        </ListItemIcon>
                        <ListItemText
                          primary={platform.name}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontWeight: 600, // Bolder text
                              fontSize: '1.25rem', // Larger font size (20px)
                              letterSpacing: '0.02em', // Slight letter spacing for readability
                              color: 'text.primary', // Ensure good contrast
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                </Box>
              </Modal>
          <Box
            sx={{
              flex: { xs: 1, md: 0.4 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 1, sm: 2, md: 4 },
              minWidth: 0,
              width: '100%',
              maxWidth: '100%',
              height: { xs: 220, sm: 320, md: 420 },
              order: { xs: -1, md: 0 } // Show 3D viewer first on mobile
            }}
          >
            <Button
              variant="contained"
              sx={{
                height: { xs: 36, sm: 44, md: 50 },
                width: { xs: 36, sm: 44, md: 50 },
                minWidth: 0,
                p: 0,
                fontSize: { xs: 18, sm: 22, md: 28 },
                backgroundColor: '#424242ff',
                color: '#55DEF1'
              }}
              onClick={() =>
                setCurrentBookIndex((prev) => (prev === 0 ? books.length - 1 : prev - 1))
              }
              aria-label="Previous Book"
            >
              <ArrowBack sx={{ fontSize: { xs: 18, sm: 22, md: 28 } }} />
            </Button>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: { xs: 220, sm: 320, md: 420 },
                width: { xs: 180, sm: 260, md: 340 },
                height: '100%',
                mx: { xs: 1, sm: 2, md: 4 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Novel3DViewer
                modelPath={books[currentBookIndex].modelPath}
                book={books[currentBookIndex]}
                title={books[currentBookIndex].title}
                description={books[currentBookIndex].viewerDescription}
              />
            </Box>
            <Button
              variant="contained"
              sx={{
                height: { xs: 36, sm: 44, md: 50 },
                width: { xs: 36, sm: 44, md: 50 },
                minWidth: 0,
                p: 0,
                fontSize: { xs: 18, sm: 22, md: 28 },
                backgroundColor: '#424242ff',
                color: '#55DEF1'
              }}
              onClick={() =>
                setCurrentBookIndex((prev) => (prev === books.length - 1 ? 0 : prev + 1))
              }
              aria-label="Next Book"
            >
              <ArrowForward sx={{ fontSize: { xs: 18, sm: 22, md: 28 } }} />
            </Button>
          </Box>
        </Box>
      </Box>
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700} color={theme.palette.primary.main}>
          WHAT READERS ARE SAYING
        </Typography>
        <Typography fontSize={{ xs: 24, sm: 30, md: 40 }} fontWeight={700} sx={{color: theme.palette.secondary.main, marginBottom: '2%'}}>
          {books[currentBookIndex].title}
        </Typography>
        
        {books[currentBookIndex].reviews && books[currentBookIndex].reviews!.length > 0 ? (
          <Box sx={{
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            height: '100%',
            gap: { xs: 2, md: 0 }
          }}>
            {books[currentBookIndex].reviews!.map((review, index) => (
              <Paper
                key={index}
                sx={{
                  flex: 1,
                  margin: { xs: '1%', md: '2%' },
                  padding: { xs: '4%', md: '2%' },
                  background: 'rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: 3,
                }}
              >
                <Box sx={{alignItems: 'center', display: 'flex', justifyContent: 'center', marginBottom: '2%', width:'100%'}}>
                  <StarFilled style={{ color: '#FFD700', fontSize: 30, marginRight: '5px' }}/>
                  <StarFilled style={{ color: '#FFD700', fontSize: 30, marginRight: '5px' }}/>
                  <StarFilled style={{ color: '#FFD700', fontSize: 30, marginRight: '5px' }}/>  
                  <StarFilled style={{ color: '#FFD700', fontSize: 30, marginRight: '5px' }}/>
                  <StarFilled style={{ color: '#FFD700', fontSize: 30, marginRight: '5px' }}/>
                </Box><br />
                <FormatQuote sx={{ fontSize: { xs: 35, md: 50 }, verticalAlign: 'top', color: 'secondary' }} />
                <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
                  "{review.text}"
                </Typography>
                <Typography fontSize={{ xs: 16, sm: 18, md: 20 }} fontWeight={600} sx={{marginTop: '5%'}}>
                  — {review.author}
                </Typography>
              </Paper>
            ))}
          </Box>
        ) : (
          // Show "Coming Soon" when no reviews available
          <Box sx={{display: 'flex', flexDirection: 'row', height: '100%', justifyContent: 'center', alignItems: 'center'}}>
            <Paper
              sx={{
                flex: 1,
                margin: { xs: '1%', md: '2%' },
                padding: { xs: '6%', md: '4%' },
                background: 'rgba(255,255,255,0.3)',
                backdropFilter: 'blur(8px)',
                boxShadow: 3,
                textAlign: 'center',
              }}
            >
              <Typography fontSize={{ xs: 28, sm: 32, md: 40 }} fontWeight={700} sx={{color: '#55DEF1', marginBottom: '2%'}}>
                Reviews Coming Soon
              </Typography>
              <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400}>
                Reader reviews will be available closer to the release date.
              </Typography>
            </Paper>
          </Box>
        )}
            <Box sx={{ lineHeight: '0.0', flex: 1.0 }}>
              <Paper
                sx={{
                  flex: 1,
                  margin: { xs: '1%', md: '2%' },
                  padding: { xs: '6%', md: '3%' },
                  background: 'rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: 3,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: { xs: 3, md: 0 }
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    fontSize={{ xs: 22, sm: 26, md: 30 }} 
                    fontWeight={700}
                    sx={{ marginBottom: '12px', lineHeight: '1.3', color: '#000000ff' }}
                  >
                    See what other readers are saying on Goodreads
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  sx={{
                    marginLeft: { xs: 0, md: '24px' },
                    padding: { xs: '12px 20px', md: '12px 24px' },
                    fontSize: { xs: '14px', md: '16px' },
                    fontWeight: '600',
                    minWidth: { xs: '100%', sm: '220px' },
                    whiteSpace: 'nowrap',
                    color: 'white',
                    backgroundColor: 'black'
                  }}
                  size="large"
                  onClick={() => window.location.href='https://www.goodreads.com/author/show/62379421.Martin_Knaggs'}
                >
                  VIEW ON GOODREADS
                </Button>
              </Paper>
            </Box>
      </Box>
      <Box id="books" sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700}>
          ABOUT THE SERIES
        </Typography><br/>
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          height: '100%',
          gap: { xs: 4, md: 0 }
        }}>
          <Box sx={{lineHeight: '0.0', flex: { xs: 1, md: 0.6 }}}>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400} sx={{marginTop: '5%'}}>
              Across twelve empires stretched across the stars, war has been waiting to ignite for over 90,000 years. An ancient evil force, patient and relentless, has returned, determined to consume everything in its path. <br /><br />
              At the center of it all stand eight destined guardians, beginning with siblings Chloe and Max. Bound by the nanites within them, hunted by powers far greater than they can imagine, they must seek out the other guardians while fighting not only to survive but to resist the corruption of the empires and the Federation's brutal rule.<br /><br />
              To stand any chance, they must also master their own abilities and grow into the power inside them, because their ascension could mean survival… or the destruction of everything.<br /><br />
              Their only guide is Vaelion, a sharp-tongued AI whose sarcasm is matched only by his survival instincts. He might keep them alive or drive them insane first.<br /><br />
              The Shard Legacy blends cinematic science fiction with psychological horror, weaving a saga of betrayal, ascension, and survival on a galactic scale.<br />
            </Typography>
          </Box>
          <Box sx={{
            flex: { xs: 1, md: 0.4 }, 
            display: 'flex', 
            flexDirection: 'row', 
            height: '100%', 
            alignItems: 'center', 
            justifyContent: 'center'
          }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                gap: 3,
                width: '100%',
                justifyItems: 'center'
              }}
            >
              {books.map((book) => (
                <Box
                  key={book.slug}
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.3s cubic-bezier(.4,2,.6,1)',
                    '&:hover': {
                      transform: 'scale(1.07)',
                      zIndex: 2,
                    },
                    boxShadow: 4,
                    borderRadius: 2,
                    overflow: 'hidden',
                    width: { xs: 180, sm: 180, md: 220 },
                    height: { xs: 270, sm: 270, md: 320 },
                    background: '#fff',
                    mb: 2,
                  }}
                  onClick={() => navigate(`/books/${book.slug}`, { state: { bookItem: book } })}
                  tabIndex={0}
                  role="button"
                  aria-label={`Go to ${book.title}`}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate(`/books/${book.slug}`, { state: { bookItem: book } });
                    }
                  }}
                >
                  <img
                    src={book.coverImageUrl}
                    alt={book.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Box sx={{display: 'flex', flexDirection: 'row', height: '100%', width: '100%'}}>
          <Box sx={{ lineHeight: '0.0', flex: 1.0 }}>
            <Paper
              sx={{
                flex: 1,
                margin: { xs: '1%', md: '2%' },
                padding: { xs: '6%', md: '3%' },
                background: '#55dff1ff',
                backdropFilter: 'blur(8px)',
                boxShadow: 3,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: { xs: 3, md: 0 }
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography 
                  fontSize={{ xs: 22, sm: 26, md: 30 }} 
                  fontWeight={700}
                  sx={{ marginBottom: '12px', lineHeight: '1.3', color: '#000000ff' }}
                >
                  Take a deep dive into the universe
                </Typography>
                <Typography 
                  fontSize={{ xs: 16, sm: 18, md: 20 }} 
                  fontWeight={400}
                  sx={{ color: '#000000ff', lineHeight: '1.4' }}
                >
                  Read about the factions, characters and more...
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  marginLeft: { xs: 0, md: '24px' },
                  padding: { xs: '12px 20px', md: '12px 24px' },
                  fontSize: { xs: '14px', md: '16px' },
                  fontWeight: '600',
                  minWidth: { xs: '100%', sm: '220px' },
                  whiteSpace: 'nowrap',
                  color: 'white',
                  backgroundColor: 'black'
                }}
                size="large"
                onClick={() => window.location.href='/universe'}
              >
                EXPLORE THE UNIVERSE
              </Button>
            </Paper>
          </Box>
        </Box>
      </Box>
      <Box sx={{
        backgroundColor: '#363636ff',
        display: 'flex',
        flexDirection: 'row',
      }}>
        <Box sx={{flex: 1}}>
          <img src={signUpImage} width='100%' height='100%' alt="A spacecraft flying through space" />
        </Box>
        <Box sx={{flex: 1, padding:'5%'}}>
          <Typography fontSize={{ xs: 16, sm: 20, md: 30 }} fontWeight={700}>
            JOIN THE FIGHT
          </Typography><br/>
          <Typography fontSize={{ xs: 16, sm: 20, md: 30 }} fontWeight={700}>
            SIGN UP FOR UPDATES
          </Typography>
          <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400} sx={{marginTop: '5%'}}>
            Get the first chapter free + exclusive launch updates.
            Be the first to know when new books drop, unlock bonus lore, and access early previews.
          </Typography>

          <SignupForm/>
        </Box>
      </Box>
      <Box id="contact-section" sx={{ height: '100%', margin: { xs: '5%', md: '10%' } }}>
        <Typography fontSize={{ xs: 32, sm: 40, md: 60 }} fontWeight={700}>
          ABOUT MARTIN
        </Typography><br/>
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          height: '100%', 
          width: '100%',
          alignItems: 'center',
          gap: { xs: 4, md: 0 }
        }}>
          <Box sx={{
            lineHeight: '0.0', 
            flex: { xs: 1, md: 0.8 },
            order: { xs: 1, md: 0 }
          }}>
            <Typography fontSize={{ xs: 18, sm: 20, md: 25 }} fontWeight={400} sx={{
              marginTop: '5%', 
              paddingRight: { xs: 0, md: '10%' }, 
              width: '100%'
            }}>
              Get in touch with Martin for inquiries about upcoming works, speaking engagements,
              or just to share your thoughts on the novels. Whether you're a fan of his thrilling
              narratives or curious about his creative process, Martin loves hearing from readers
              and fellow storytellers. Reach out via email or connect with him on social media
              to stay updated on his latest projects and events.
            </Typography>
            <Box sx={{
              margin: { xs: '1%', md: '2%' },
              display: 'flex',
              flexDirection: 'row'
            }}>
              <Button 
                href='https://www.instagram.com/mlknaggs/'
                target="_blank" rel="noopener noreferrer"
              >
                <Instagram sx={{
                  fontSize: '48px'
                }}/>
              </Button>
              <Button 
                href='https://www.facebook.com/profile.php?id=61578142014072&rdid=l6rKN1pkkfAXrcoM'
                target="_blank" rel="noopener noreferrer"
              >
                <Facebook sx={{
                  fontSize: '48px'
                }}/>
              </Button>
              <Button 
                href='https://www.tiktok.com/@m.l.knaggs'
                target="_blank" rel="noopener noreferrer"
              >
                <TikTokOutlined style={{
                  fontSize: '44px'
                }}/>
              </Button>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                marginTop: '5%',
                marginRight: { xs: 0, md: '5%' },
                padding: { xs: '15px 30px', md: '20px 48px' },
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                minWidth: { xs: '100%', sm: '220px' }
              }}
              size="large"
              href='/about'
            >
              READ MORE
            </Button>
            </Box>
            <Box sx={{
              flex: { xs: 1, md: 0.2 }, 
              display: 'flex', 
              flexDirection: 'row', 
              height: '100%',
              justifyContent: 'center',
              order: { xs: 0, md: 1 }
            }}>
              <img 
                src="/images/martinknaggs.jpeg" 
                alt="Martin Knaggs" 
                style={{
                  width: 300,
                  height: 300,
                  borderRadius: '150px',
                  marginTop: '5%',
                  maxWidth: '100%'
                }} 
              />
            </Box>
        </Box>
      </Box>

    </Box>
  );
};

export default HomeSection;
